import React, { useState } from 'react';
import { Edit2, Info, HeartPulse, Sparkles, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
const pulseScoreData = [{
  month: 'Oct',
  score: 68,
  isKeyEvent: false
}, {
  month: 'Nov',
  score: 72,
  isKeyEvent: false
}, {
  month: 'Dec',
  score: 75,
  isKeyEvent: false
}, {
  month: 'Jan',
  score: 78,
  event: 'Private financials show strong revenue growth',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 80,
  isKeyEvent: false
}, {
  month: 'Mar',
  score: 82,
  isKeyEvent: false
}];
const healthScoreData = [{
  month: 'Oct',
  score: 86
}, {
  month: 'Nov',
  score: 87,
  event: 'Positive trade references received',
  isKeyEvent: true
}, {
  month: 'Dec',
  score: 88
}, {
  month: 'Jan',
  score: 90,
  event: 'Strong financials and low debt',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 89
}, {
  month: 'Mar',
  score: 88
}];
const paymentScoreData = [{
  month: 'Oct',
  score: 72
}, {
  month: 'Nov',
  score: 70
}, {
  month: 'Dec',
  score: 68,
  event: 'Seasonal payment slowdown',
  isKeyEvent: true
}, {
  month: 'Jan',
  score: 67
}, {
  month: 'Feb',
  score: 69,
  event: 'DBT increased slightly',
  isKeyEvent: true
}, {
  month: 'Mar',
  score: 68
}];
function PositiveIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-2855" data-mp="6cwq4p2l1hc3">
      <div className="w-1 h-1.5 bg-green-500 rounded-sm" data-id="element-2856" data-mp="6fiy2ptdxyuu" />
      <div className="w-1 h-2.5 bg-green-500 rounded-sm" data-id="element-2857" data-mp="adgofvyw6qzt" />
      <div className="w-1 h-3.5 bg-green-500 rounded-sm" data-id="element-2858" data-mp="fe39r70j389k" />
    </div>;
}
function NegativeIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-2859" data-mp="kc4t7t1v99lk">
      <div className="w-1 h-1.5 bg-red-500 rounded-sm" data-id="element-2860" data-mp="kalck1caswj9" />
      <div className="w-1 h-2.5 bg-red-500 rounded-sm" data-id="element-2861" data-mp="2gh42ge4yb1vg" />
      <div className="w-1 h-3.5 bg-red-500 rounded-sm" data-id="element-2862" data-mp="2bgzq90b2stb1" />
    </div>;
}
function ScoreTooltip({
  active,
  payload,
  label,
  color
}: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dotColor = data.score !== undefined ? getDotColor(data.score) : color;
    return <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-4 py-3 max-w-[250px]" data-id="element-2863" data-mp="2z6110u7dsgjh">
        <div className="flex items-center justify-between mb-2" data-id="element-2864" data-mp="2v5i5oq6a4scj">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2865" data-mp="2v7beoc4d7onp">
            {label}
          </p>
          <div className="flex items-center gap-1.5" data-id="element-2866" data-mp="345wtbw6pujj7">
            <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: dotColor
          }} data-id="element-2867" data-mp="3466k3lzl4upy" />
            <span className="text-sm font-bold text-gray-900" data-id="element-2868" data-mp="19s5qbfiw6xwq">
              {payload[0].value}
            </span>
          </div>
        </div>
        {data.event && <p className="text-sm text-gray-700 leading-snug border-t border-gray-100 pt-2 mt-1" data-id="element-2869" data-mp="3lhbw93l4h7pk">
            {data.event}
          </p>}
      </div>;
  }
  return null;
}
function getDotColor(score: number) {
  if (score <= 30) return '#ef4444';
  if (score <= 70) return '#eab308';
  return '#22c55e';
}
const CustomDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  if (payload.isKeyEvent) {
    return <g data-id="element-2870" data-mp="37p5mq0ch5yz3">
        <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0.2} className="animate-pulse" data-id="element-2871" data-mp="37lhur5mohkmh" />
        <circle cx={cx} cy={cy} r={6} fill="#ffffff" stroke={color} strokeWidth={3} data-id="element-2872" data-mp="2xqg4u7kkweiu" />
      </g>;
  }
  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} strokeWidth={2} data-id="element-2873" data-mp="vya4hdp3r3jy" />;
};
const CustomActiveDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  return <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} data-id="element-2874" data-mp="2o0mhzmseaqrd" />;
};
export function OverviewTab() {
  const [healthExpanded, setHealthExpanded] = useState(true);
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2875" data-mp="292amvkko7q2b">
      {/* Top Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2876" data-mp="24bub04wi0ci2">
        {/* Credit Settings */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-1" data-id="element-2877" data-mp="20a0f09x8zwo8">
          <div className="flex justify-between items-center mb-6" data-id="element-2878" data-mp="1vaw155zdpark">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-2879" data-mp="1vcqpku9uzl0e">Credit Settings</h3>
            <button className="w-8 h-8 bg-indigo-600 hover:bg-indigo-700 rounded-full flex items-center justify-center text-white transition-colors" data-id="element-2880" data-mp="1qahxe04t8mzc">
              <Edit2 className="w-4 h-4" data-id="element-2881" data-mp="1b91lloo73ji5" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-2882" data-mp="1bavz4hx509c7">
            <div data-id="element-2883" data-mp="1g6kxwj1c1m7g">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2884" data-mp="1g61gd3flgzty">
                Credit Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-2885" data-mp="1l54mkr7b9c3i">$50,000</p>
            </div>
            <div data-id="element-2886" data-mp="1q5ai4igpbiqv">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2887" data-mp="1q3poqw64i5ul">
                Recommended Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-2888" data-mp="xh4u9mucpdmt">$50,000</p>
            </div>
            <div data-id="element-2889" data-mp="xf20crq2v0ct">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2890" data-mp="12gr6pu2cupuk">
                Outstanding Balance
              </p>
              <p className="text-sm text-gray-600" data-id="element-2891" data-mp="17898ulg46wh4">$12,450</p>
            </div>
            <div data-id="element-2892" data-mp="9sbybqdqb5s7">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2893" data-mp="9qr4y435hsvx">
                AP Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-2894" data-mp="epc4e1akw8jb">Angela Martin</p>
            </div>
            <div data-id="element-2895" data-mp="6owbrjqydvv8">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2896" data-mp="6oe8421yofda">
                Terms
              </p>
              <p className="text-sm text-gray-600" data-id="element-2897" data-mp="1p1kvg76h6a6">Net 30</p>
            </div>
            <div data-id="element-2898" data-mp="gfh973uw4uck">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2899" data-mp="gi3cusenuppi">
                Purchasing Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-2900" data-mp="3jwh93hen1yp8">Dwight Schrute</p>
            </div>
            <div data-id="element-2901" data-mp="3jyk7aopy2y51">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2902" data-mp="3exwb7omuzr3y">
                Late Pay Penalty
              </p>
              <p className="text-sm text-gray-600" data-id="element-2903" data-mp="3tvycfs6f40i1">1.5%</p>
            </div>
            <div data-id="element-2904" data-mp="3ovapbhlb83t3">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2905" data-mp="3ousk3vgo6f91">
                Company Officer
              </p>
              <p className="text-sm text-gray-600" data-id="element-2906" data-mp="1934pi6p4oyte">Michael Scott</p>
            </div>
            <div data-id="element-2907" data-mp="144te3rj6qg92">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2908" data-mp="145bjbdnts4t4">
                Date Last Approved
              </p>
              <p className="text-sm text-gray-600" data-id="element-2909" data-mp="1brgoopomci3z">2/20/2026</p>
            </div>
            <div data-id="element-2910" data-mp="1gm46k5p4ae9l">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2911" data-mp="12oflawr51540">
                Group
              </p>
              <p className="text-sm text-gray-600" data-id="element-2912" data-mp="16prcv7tko0eu">Wholesaler</p>
            </div>
          </div>
        </div>

        {/* Pulse Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-2 flex flex-col" data-id="element-2913" data-mp="1ugwrxkrouoo3">
          <div className="flex items-center gap-2 mb-4" data-id="element-2914" data-mp="1ze6tfyscqqsa">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2915" data-mp="1lntkpu7mz81w">Pulse Score</h3>
          </div>
          <div className="flex gap-6 mb-6" data-id="element-2916" data-mp="1pon4qchyk2pi">
            <div className="bg-green-100 rounded-lg p-4 flex flex-col items-center justify-center min-w-[120px] shrink-0" data-id="element-2917" data-mp="1pnc60r3ddl59">
              <span className="text-4xl font-bold text-gray-900" data-id="element-2918" data-mp="2dcd8zytfo435">72</span>
              <span className="text-sm font-medium text-gray-800 mt-1" data-id="element-2919" data-mp="2i6r3lz09u76r">
                Low Risk
              </span>
            </div>
            <p className="text-gray-600 text-base leading-relaxed pt-2" data-id="element-2920" data-mp="1ontpxjgjxgny">
              Pulse Score of 72 indicates low risk. Score has shown volatility
              over the past year but recently stabilized in the healthy range.
            </p>
          </div>
          <div className="flex-1 min-h-[200px] relative" data-id="element-2921" data-mp="1er75uww1utmz">
            <div className="absolute top-0 right-0 flex items-center gap-2 text-xs text-gray-500 z-10" data-id="element-2922" data-mp="2cdxtjqtqpgey">
              <div className="w-3 h-3 bg-black rounded-sm" data-id="element-2923" data-mp="27dru3oh8apmb"></div>
              Pulse Score
            </div>
            <ResponsiveContainer width="100%" height="100%" data-id="element-2924" data-mp="22ef815y6lmks">
              <LineChart data={pulseScoreData} margin={{
              top: 20,
              right: 10,
              left: -20,
              bottom: 10
            }} data-id="element-2925" data-mp="1xg5fl33ud02v">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2926" data-mp="lzx7yn9jyykc" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} dy={10} data-id="element-2927" data-mp="32neporln1sy1" />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-2928" data-mp="3cf19hcc1l0ce" />
                <Tooltip content={<ScoreTooltip data-id="element-2930" data-mp="3vb13esokydym" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2,
                strokeDasharray: '4 4'
              }} data-id="element-2929" data-mp="3vc2douxruwfa" />
                <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="4 4" data-id="element-2931" data-mp="20yl0a0r2zfxz" />
                <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="4 4" data-id="element-2932" data-mp="2uk2s60nfo5u2" />
                <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2934" data-mp="34jbeq8fyyi6e" />} activeDot={<CustomActiveDot data-id="element-2935" data-mp="38j2f93vasco4" />} data-id="element-2933" data-mp="2zhm3ossqy4ib" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2936" data-mp="3igqb5x4n3q7v">
        <button onClick={() => setHealthExpanded(!healthExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-2937" data-mp="3n7qata558me2">
          <div className="flex items-center gap-3" data-id="element-2938" data-mp="3s65bszq76u">
            <HeartPulse className="w-5 h-5 text-gray-700" data-id="element-2939" data-mp="xo1qxg8ygl" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2940" data-mp="5350siq3sijz">Health Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-green-500 bg-white ml-2" data-id="element-2941" data-mp="1pknrvah7mt05">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" data-id="element-2942" data-mp="1kjzzakdc243t"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-2943" data-mp="1gixyhz3rpjzq">88</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-2944" data-mp="1bkmqltx19jk5" />
          </div>
          {healthExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-2945" data-mp="248sbcgo378jz" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-2946" data-mp="1zi25717u71o8" />}
        </button>

        {healthExpanded && <div className="p-5" data-id="element-2947" data-mp="1uk0o2ltz1cfe">
            <p className="text-base text-gray-600 mb-6" data-id="element-2948" data-mp="1uipicggytuln">
              DUNDER MIFFLIN PAPER COMPANY has a 1.2% probability of insolvency
              or bankruptcy in the next 12 months.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2949" data-mp="2n6ldhgqvzegb">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2950" data-mp="366831k6rbxrx">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2951" data-mp="3b1wuf08mi8d5">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2952" data-mp="31d6lf4f61k4n">
                  <div className="flex items-start gap-2" data-id="element-2953" data-mp="31bvfoz25u2aw">
                    <NegativeIndicator data-id="element-2954" data-mp="2nd44bkeomuqg" />
                    <span className="text-sm text-gray-600" data-id="element-2955" data-mp="2nc2jwpbs0vw2">
                      Recent DBT increase trend (67% MoM)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2956" data-mp="2ieaohlbxarn4">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2957" data-mp="2ign96692fg3v">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2958" data-mp="glwcf2w28hem">
                  <div className="flex items-start gap-2" data-id="element-2959" data-mp="gkuwol43n6qk">
                    <PositiveIndicator data-id="element-2960" data-mp="2161ds21fzlgp" />
                    <span className="text-sm text-gray-600" data-id="element-2961" data-mp="1x3my1qt2l1tn">
                      Strong financials (77 years in business, solid Z-Score)
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2962" data-mp="2aww3xhht757f">
                    <PositiveIndicator data-id="element-2963" data-mp="263jghaov94g9" />
                    <span className="text-sm text-gray-600" data-id="element-2964" data-mp="262i2axck01ub">
                      Stable revenue and employee growth
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-2965" data-mp="2tu5ncyn4wioo">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2966" data-mp="2trjhd3fw6b8k">
                  <LineChart data={healthScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2967" data-mp="2ow44oetmeldq">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2968" data-mp="ww80t1y7qy0j" />
                    <XAxis dataKey="month" hide data-id="element-2969" data-mp="15wedilm37tyn" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-2970" data-mp="15wo2c5vebv2r" />
                    <Tooltip content={<ScoreTooltip color="#22c55e" data-id="element-2972" data-mp="n4lv1l4n9r56" />} data-id="element-2971" data-mp="n552g7wo4x4y" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2974" data-mp="3qk9gsaxeszzx" />} activeDot={<CustomActiveDot data-id="element-2975" data-mp="3vkoreuqb3te7" />} data-id="element-2973" data-mp="n3tx43nkrhbq" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2976" data-mp="3j3dzchoadxe7">
        <button onClick={() => setPaymentExpanded(!paymentExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-2977" data-mp="3e2gg01rjixb4">
          <div className="flex items-center gap-3" data-id="element-2978" data-mp="35565wjqgle7s">
            <CreditCard className="w-5 h-5 text-gray-700" data-id="element-2979" data-mp="30c381zenfils" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2980" data-mp="30aiiypb3srvb">Payment Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-yellow-500 bg-white ml-2" data-id="element-2981" data-mp="2vdjmw17vqauh">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" data-id="element-2982" data-mp="2qccfi39nfdoc"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-2983" data-mp="2leav9f0hl8ba">68</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-2984" data-mp="2he0rm9062tzl" />
          </div>
          {paymentExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-2985" data-mp="2bp3ygltnse4g" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-2986" data-mp="1xta0gxufb964" />}
        </button>

        {paymentExpanded && <div className="p-5" data-id="element-2987" data-mp="22swg1dxq51i3">
            <p className="text-base text-gray-600 mb-6" data-id="element-2988" data-mp="2qfur1w32rh8m">
              DUNDER MIFFLIN PAPER COMPANY has a 12.3% probability of late
              payment, indicating moderate risk.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2989" data-mp="2gr33e051rj3i">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2990" data-mp="2lp4oeqm19shm">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2991" data-mp="39f8paq7u7e4x">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2992" data-mp="3e7i8lneg5fy8">
                  <div className="flex items-start gap-2" data-id="element-2993" data-mp="3nkirw9n0w401">
                    <NegativeIndicator data-id="element-2994" data-mp="3nixxck0pkl26" />
                    <span className="text-sm text-gray-600" data-id="element-2995" data-mp="3jjfahipbtzwp">
                      Average 60+ day overdue balance ratio
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2996" data-mp="17aek503zigz">
                    <NegativeIndicator data-id="element-2997" data-mp="3scimrno8k6pr" />
                    <span className="text-sm text-gray-600" data-id="element-2998" data-mp="3sdk76ir565k5">
                      Recent DBT increase (6 to 10 days)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2999" data-mp="3ekcesov8l61e">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-3000" data-mp="39l9bb8uwn7vj">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-3001" data-mp="ts0mjvkyoaik">
                  <div className="flex items-start gap-2" data-id="element-3002" data-mp="ova63758vcwm">
                    <PositiveIndicator data-id="element-3003" data-mp="owv1eyzduzvj" />
                    <span className="text-sm text-gray-600" data-id="element-3004" data-mp="2jbinmg2bcg4n">
                      DBT well below industry average
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-3005" data-mp="2nahlxhtjhen4">
                    <PositiveIndicator data-id="element-3006" data-mp="2savmyfwq3obn" />
                    <span className="text-sm text-gray-600" data-id="element-3007" data-mp="2s9ubwbfpj1tx">
                      Your AR consistently 2-4 days
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-3008" data-mp="3701ucr5pskum">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3009" data-mp="372o3gv8978ey">
                  <LineChart data={paymentScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-3010" data-mp="3b3gd3pl1ffbr">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-3011" data-mp="1lmp42lgsh601" />
                    <XAxis dataKey="month" hide data-id="element-3012" data-mp="19czb0qdvcpg6" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-3013" data-mp="19c7k3sv7vfw8" />
                    <Tooltip content={<ScoreTooltip color="#eab308" data-id="element-3015" data-mp="1s37e58hkjfwd" />} data-id="element-3014" data-mp="1s3qljv9lelw5" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-3017" data-mp="1j2izqco9jdiy" />} activeDot={<CustomActiveDot data-id="element-3018" data-mp="bpqn6oqeb4b0" />} data-id="element-3016" data-mp="1s1nv5a4j2qqw" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}